import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Toaster } from "@/components/ui/Toaster";
import "./style.css";
import { IMAGES } from "@/assets/IMAGES";
import { Tooltip, TooltipContent, TooltipTrigger } from "../MobileToolTip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { extractLinks } from "@/utils/Helper";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { ResourceTypes } from "@/app/types/roles";
import { useDispatch } from "react-redux";
import { setSidePanelDispatch } from "@/features/SidePanelSlice";

const DashboardLayout: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [sidePanel, setSidePanel] = useState<ResourceTypes[]>([]);
  const [backtogym, setBacktogym] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  // Fetch and set sidePanel from localStorage
  useEffect(() => {
    // Function to update side panel
    const updateSidePanel = () => {
      const sidepanel = localStorage.getItem("sidePanel");
      if (sidepanel) {
        try {
          const decodedSidepanel = JSON.parse(atob(sidepanel));
          const links = extractLinks(decodedSidepanel);
          setSidePanel(decodedSidepanel);
          setBacktogym(links[0]);
        } catch (error) {
          console.error("Error decoding Base64 string sidepanel:", error);
        }
      }
    };

    // Initial update
    updateSidePanel();

    // Add event listener for user switching
    const handleUserSwitch = () => {
      updateSidePanel();
    };

    window.addEventListener('userSwitched', handleUserSwitch);

    // Cleanup
    return () => {
      window.removeEventListener('userSwitched', handleUserSwitch);
    };
  }, []);

  // Determine if the link is active
  const isActiveLink = (targetPath: string) => {
    if (pathname === targetPath) return true;
    if (pathname.startsWith(targetPath)) {
      const remainingPath = pathname.slice(targetPath.length);
      return remainingPath === "" || remainingPath.startsWith("/");
    }
    return false;
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
    dispatch(setSidePanelDispatch(!isSidebarOpen))
  };

  return (
    <div className="font-poppins flex h-full w-full relative">
      <nav
        className={`relative border-r text-black shadow-md h-screen custom-scrollbar-right transition-all duration-300 ${isSidebarOpen ? "w-[275px]" : "w-[72px]"
          }`}
      >
        <div
          style={{ direction: "ltr" }}
          className={` flex h-16 items-center ${!isSidebarOpen ? "justify-center" : "justify-between"} px-4 border-gradient sticky top-0 z-30 bg-white`}
        >
          <Link
            to={sidePanel[0]?.link}
            className="flex items-center gap-2 font-semibold "
          >
            <img
              src={IMAGES.logo_icon}
              className={`${isSidebarOpen ? "h-[3rem] w-[3rem]" : "h-[3rem] w-[3rem]"}"`}
              alt="Dashboard"
            />
            <span
              className={`${!isSidebarOpen && "hidden"
                } text-[1.3rem] text-center font-semibold text-primary`}
            >
              QMS{" "}
            </span>
          </Link>

          <button
            onClick={toggleSidebar}
            className={`absolute  top-[3.2rem] -right-1.5  z-50 size-6 flex items-center rounded-md bg-white transition  border-[1px] border-gradient p-0 `}
          >
            {isSidebarOpen ? (
              <ChevronLeft className=" text-gray-700 font-bold pr-1" />
            ) : (
              <ChevronRight className=" text-gray-700 font-bold pr-1" />
            )}
          </button>
        </div>

        <div
          style={{ direction: "ltr" }}
          className="flex flex-col gap-2 px-2 py-2"
        >
          {sidePanel?.map((item: any, i: number) => (
            <div
              key={Number(i)}
              className={`${isSidebarOpen ? "" : "justify-center items-center flex"}`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  {item.children?.length === 0 && item.link !== "/" && (
                    <Link
                      to={item.link}
                      className={`group mb-1 flex items-center gap-2 rounded-md p-1 transition-colors ${isSidebarOpen
                        ? "justify-start text-sm"
                        : "justify-center text-lg"
                        } ${isActiveLink(item.link)
                          ? "bg-primary"
                          : "hover:bg-secondary"
                        }`}
                    >
                      <div
                        className={` w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary "} rounded-lg justify-center flex items-center`}
                      >
                        <i
                          className={`${item.icon}  ${isActiveLink(item.link) ? "text-secondary" : "text-primary stroke-current"}`}
                        ></i>
                      </div>
                      <span
                        className={`${!isSidebarOpen && "hidden"} ${isActiveLink(item.link)
                          ? "text-secondary"
                          : "text-gray-500 group-hover:text-primary"
                          }`}
                      >
                        {item.name}
                      </span>
                    </Link>
                  )}
                </TooltipTrigger>
                {!isSidebarOpen && (
                  <TooltipContent
                    className="bg-secondary text-primary border-none"
                    side="right"
                  >
                    <p>{item.name}</p>
                  </TooltipContent>
                )}
              </Tooltip>

              {item.children &&
                item.children?.length > 0 &&
                (isSidebarOpen ? (
                  <Accordion type="single" collapsible >
                    <AccordionItem value="item-1 !border-none" id="accordion">
                      <AccordionTrigger
                        className={`group flex items-center gap-2 rounded-md p-1 transition-colors !no-underline ${isSidebarOpen ? "justify-between text-sm" : "justify-center text-lg "} `}
                      >
                        <div className="text-gray-500 flex items-center gap-2">
                          <div
                            className={` w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary "} rounded-lg justify-center flex items-center`}
                          >
                            <i
                              className={`${item.icon}  ${isActiveLink(item.link) ? "text-black" : "text-primary stroke-current"}`}
                            ></i>
                          </div>
                          <span
                            className={`${!isSidebarOpen && "hidden"} font-normal`}
                          >
                            {item.name}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="ml-7 flex flex-col  gap-1 mt-2 [data-state=closed]:animate-accordion-up [data-state=open]:animate-accordion-down ">
                          {item.children?.map((child: any, index: number) => (
                            <Link
                              key={Number(index)}
                              to={child.link}
                              className={`justify-start rounded-md px-3  py-1 ${isActiveLink(child.link) ? "bg-primary text-secondary" : "hover:bg-muted"} ${!isSidebarOpen && "hidden"}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Popover>
                    <PopoverTrigger className="group flex items-center gap-2 rounded-md p-1 transition-colors justify-center">
                      <div
                        className={`w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary"} rounded-lg flex items-center justify-center`}
                      >
                        <i
                          className={`${item.icon} ${isActiveLink(item.link) ? "text-black" : "text-primary"}`}
                        ></i>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="bg-white text-primary border-none">
                      <div className="flex flex-col gap-1">
                        {item.children.map((child: any, index: number) => (
                          <Link
                            key={Number(index)}
                            to={child.link}
                            className={`justify-start rounded-md px-3 py-1 ${isActiveLink(child.link) ? "bg-secondary text-primary" : "hover:bg-muted"}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                ))}
            </div>
          ))}
        </div>
      </nav>
      <div className="flex-1 h-screen custom-scrollbar  w-[calc(100%-275px)]">
        <Header />
        <main className="bg-outletcolor min-h-[calc(100vh-64px)]">
          <div className="w-full mx-auto max-w-[2000px]">
            <Outlet />
            <Toaster />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
